import { Card, type CardProps, Flex } from 'antd';
import clx from 'classnames';
import { darken, lighten } from 'polished';
import type * as React from 'react';
import styled from 'styled-components';
import { FlexRow } from '../row/FlexRow.tsx';
import {
  useConfigurationCardLayout,
  useConfigurationCardRegister,
} from './ConfigurationCardContext.tsx';
import { ConfigurationCardDescriptionPopover } from './ConfigurationCardDescriptionPopover.tsx';

const LeftBarCardWrapper = styled.div`
  border-left: 4px solid ${props => props.theme.colorPrimary};
  border-radius: 4px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;

  &.dense {
    border-left: none;
    border-radius: 0;

    /* Compensa il padding laterale 24px del contenuto della card flat: le
       sub-card aggiungono già il proprio inset 12px (head/body), quindi il
       loro contenuto resta a 24px dal bordo, come i campi della card. */
    margin-inline: -12px;

    border-bottom: 1px solid ${props => darken(0.1, props.theme.colorBgLayout)};
    padding-bottom: 6px;
    /* Distanza tra sub-card incorporata: niente VerticalSpace a mano. */
    margin-bottom: 16px;

    /* L'ultima sub-card non mostra il separatore inferiore né la spaziatura. */
    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
      margin-bottom: 0;
    }
  }
`;

const ConfigurationSubCardIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: ${props => props.theme.colorPrimary};
  margin-right: 8px;
  font-size: 24px;
`;
const ConfigurationSubCardTitleWrapper = styled(Flex)`
  justify-content: center;
  .title, .description {
    margin: 0;
    padding: 0;
  }
  .title {
    font-size: 16px;
    font-weight: 600;
    line-height: 19px;
    min-height: 22px;
  }
  .description {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.2;
    color: ${props => props.theme.colorTextSecondary};
  }
`;

const ConfigurationSubCardFinalText = styled.div`
  padding-top: 16px;
  color: ${props => props.theme.colorTextSecondary};
`;

const StyledConfigurationSubCard = styled(Card)`
  border: 1px solid ${props => darken(0.1, props.theme.colorBgLayout)};
  border-left: 0;
  border-radius: 4px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;

  > .ant-card-head {
    border-bottom: 1px solid ${props => darken(0.1, props.theme.colorBgLayout)};
    background-color: ${props => lighten(0.015, props.theme.colorBgLayout)};
    border-top-right-radius: 3px;
    .ant-card-head-wrapper {
      padding: 16px 0;
    }
    /* Permette al titolo di andare a capo invece di essere troncato con ellipsis */
    .ant-card-head-title {
      overflow: visible;
      white-space: normal;
      text-overflow: unset;
    }
  }

  &.full-width-body > .ant-card-body {
    padding: 0;

    /* TableData interno: rimuoviamo shadow/border-radius del wrapper così la
       tabella risulta flush con i bordi della SubCard senza creare un secondo
       filo visibile sotto la tabella. */
    .table-data-responsive-card {
      box-shadow: none;
      border-radius: 0;
    }
  }

  /* Vista densa: header compatto, icona e titolo ridotti, descrizione come
     popover ⓘ accanto al titolo. */
  &.dense {
    border: none;

    > .ant-card-head {
      padding-inline: 12px;
      min-height: 32px;
      border-bottom: none;
      background-color: transparent;
      border-top-right-radius: 0;
      .ant-card-head-wrapper {
        padding: 5px 0;
      }
    }

    /* 22px = pareggia altezza dei Tag in header (StyledTag) */
    ${ConfigurationSubCardIconWrapper} {
      width: 22px;
      height: 22px;
      font-size: 22px;
      margin-right: 8px;
    }

    ${ConfigurationSubCardTitleWrapper} {
      .title {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
        font-size: 14px;
        /* Annulla il min-height e line-height (Tag nel titolo) */
        line-height: 1;
        min-height: 0;
      }
    }

    > .ant-card-body {
      padding: 12px;
    }
  }
`;

type ConfigurationSubCardTitleProps =
  | { title: string; anchorTitle?: string }
  | { title: React.ReactNode; anchorTitle: string };

export type ConfigurationSubCardProps = Omit<CardProps, 'title'> &
  ConfigurationSubCardTitleProps & {
    icon: React.ReactNode;
    description?: React.ReactNode;
    /** Azzera il padding del body della sub-card (utile quando contiene una tabella) */
    fullWidthBody?: boolean;
    /**
     * Contenuto opzionale mostrato a destra nell'header (es. chip di stato).
     */
    summary?: React.ReactNode;
    /**
     * Nota opzionale in chiusura di card (testo secondario, distanziato dal
     * contenuto), per esplicitare il comportamento di default o di fallback
     * quando i campi non vengono valorizzati.
     */
    finalText?: React.ReactNode;
  };

/**
 * Sub-card di configurazione con barra laterale colorata, da usare annidata
 * dentro una ConfigurationCard. Si auto-registra come voce figlia nell'Anchor
 * del ConfigurationCardGroup, apparendo indentata sotto la card genitore.
 *
 * Dentro un group `dense` riduce le spaziature e mostra la `description` come
 * popover ⓘ. Le sub-card non sono collassabili (a differenza della
 * `ConfigurationCard`): restano sempre aperte.
 *
 * Passare `fullWidthBody` per azzerare il padding del body, ad esempio quando
 * il contenuto è una tabella che deve risultare flush con i bordi della card.
 *
 * `finalText` aggiunge una nota di chiusura in testo secondario sotto il
 * contenuto, per esplicitare il comportamento di fallback dei campi.
 */
export function ConfigurationSubCard(props: ConfigurationSubCardProps) {
  const {
    title,
    icon,
    description,
    anchorTitle,
    fullWidthBody,
    summary,
    finalText,
    className,
    children,
    ...otherProps
  } = props;

  const anchorId = useConfigurationCardRegister(
    anchorTitle ?? (title as string),
    {
      isSubCard: true,
      icon,
      label: typeof title === 'string' ? title : anchorTitle,
    },
  );
  const { dense } = useConfigurationCardLayout();

  return (
    <LeftBarCardWrapper className={clx({ dense })}>
      <StyledConfigurationSubCard
        bordered
        id={anchorId}
        {...otherProps}
        className={clx(className, {
          'full-width-body': fullWidthBody,
          dense,
        })}
        title={
          <FlexRow
            left={
              <ConfigurationSubCardIconWrapper aria-hidden="true">
                {icon}
              </ConfigurationSubCardIconWrapper>
            }
            right={
              summary != null ? (
                <Flex align="center" justify="flex-end" gap={8} wrap="wrap">
                  {summary}
                </Flex>
              ) : undefined
            }
          >
            <ConfigurationSubCardTitleWrapper vertical gap={4}>
              <div className="title">
                {title}
                {dense && description != null && (
                  <ConfigurationCardDescriptionPopover
                    description={description}
                  />
                )}
              </div>
              {!dense && description != null && (
                <div className="description">{description}</div>
              )}
            </ConfigurationSubCardTitleWrapper>
          </FlexRow>
        }
      >
        {children}
        {finalText != null && (
          <ConfigurationSubCardFinalText>
            {finalText}
          </ConfigurationSubCardFinalText>
        )}
      </StyledConfigurationSubCard>
    </LeftBarCardWrapper>
  );
}
